import React, { useState } from 'react';
import { X, Calendar, User, Mail, Phone, Car, MessageSquare, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { mockBookAppointment } from '../mock';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

export const BookingForm = ({ isOpen, onClose, onNext }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    vehicleMake: '',
    vehicleModel: '',
    vehicleYear: '',
    service: '',
    preferredDate: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const serviceOptions = [
    { value: 'diagnostic', label: t('services_list.diagnostic') },
    { value: 'ecu', label: t('services_list.ecu') },
    { value: 'electrical', label: t('services_list.electrical') },
    { value: 'module', label: t('services_list.module') },
    { value: 'sensor', label: t('services_list.sensor') },
    { value: 'oil-change', label: t('services_list.oil') },
    { value: 'battery-boost', label: t('services_list.battery') },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const result = await mockBookAppointment(formData);
      setFormData({
        name: '',
        email: '',
        phone: '',
        vehicleMake: '',
        vehicleModel: '',
        vehicleYear: '',
        service: '',
        preferredDate: '',
        message: ''
      });
      
      setTimeout(() => {
        onClose();
        onNext(formData);
      }, 500);
    } catch (error) {
      toast.error('Failed to submit appointment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="bg-neutral-900 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-neutral-800">
        {/* Header */}
        <div className="sticky top-0 bg-neutral-900 border-b border-neutral-800 p-6 flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-white">{t('booking_title')}</h2>
            <p className="text-neutral-400 mt-1">{t('booking_subtitle')}</p>
          </div>
          <button
            onClick={onClose}
            className="text-neutral-400 hover:text-white transition-colors p-2 hover:bg-neutral-800 rounded-lg"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Personal Information */}
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-lime-400 flex items-center space-x-2">
              <User className="h-5 w-5" />
              <span>{t('booking_personal')}</span>
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-neutral-200">{t('booking_name')}</Label>
                <Input
                  id="name"
                  required
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="bg-neutral-800 border-neutral-700 text-white focus:border-lime-500"
                  placeholder="John Doe"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="text-neutral-200">{t('booking_phone')}</Label>
                <Input
                  id="phone"
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="bg-neutral-800 border-neutral-700 text-white focus:border-lime-500"
                  placeholder="+555 123-4567"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-neutral-200">{t('booking_email')}</Label>
              <Input
                id="email"
                type="email"
                required
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                className="bg-neutral-800 border-neutral-700 text-white focus:border-lime-500"
                placeholder="john@example.com"
              />
            </div>
          </div>

          {/* Vehicle Information */}
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-lime-400 flex items-center space-x-2">
              <Car className="h-5 w-5" />
              <span>{t('booking_vehicle')}</span>
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="vehicleYear" className="text-neutral-200">{t('booking_year')}</Label>
                <Input
                  id="vehicleYear"
                  required
                  value={formData.vehicleYear}
                  onChange={(e) => handleChange('vehicleYear', e.target.value)}
                  className="bg-neutral-800 border-neutral-700 text-white focus:border-lime-500"
                  placeholder="2020"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="vehicleMake" className="text-neutral-200">{t('booking_make')}</Label>
                <Input
                  id="vehicleMake"
                  required
                  value={formData.vehicleMake}
                  onChange={(e) => handleChange('vehicleMake', e.target.value)}
                  className="bg-neutral-800 border-neutral-700 text-white focus:border-lime-500"
                  placeholder="Toyota"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="vehicleModel" className="text-neutral-200">Model *</Label>
                <Input
                  id="vehicleModel"
                  required
                  value={formData.vehicleModel}
                  onChange={(e) => handleChange('vehicleModel', e.target.value)}
                  className="bg-neutral-800 border-neutral-700 text-white focus:border-lime-500"
                  placeholder="Camry"
                />
              </div>
            </div>
          </div>

          {/* Service & Date */}
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-lime-400 flex items-center space-x-2">
              <Calendar className="h-5 w-5" />
              <span>{t('booking_details')}</span>
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="service" className="text-neutral-200">{t('booking_service')}</Label>
                <Select value={formData.service} onValueChange={(value) => handleChange('service', value)} required>
                  <SelectTrigger className="bg-neutral-800 border-neutral-700 text-white focus:border-lime-500">
                    <SelectValue placeholder={t('booking_service_placeholder')} />
                  </SelectTrigger>
                  <SelectContent className="bg-neutral-800 border-neutral-700 text-gray-200">
                  {serviceOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
                </Select>
              </div>

             
            </div>
          </div>

          {/* Message */}
          <div className="space-y-2">
            <Label htmlFor="message" className="text-neutral-200 flex items-center space-x-2">
              <MessageSquare className="h-4 w-4" />
              <span>{t('booking_message')}</span>
            </Label>
            <Textarea
              id="message"
              value={formData.message}
              onChange={(e) => handleChange('message', e.target.value)}
              className="bg-neutral-800 border-neutral-700 text-white focus:border-lime-500 min-h-[100px]"
              placeholder={t('booking_message_placeholder')}
            />
          </div>

          {/* Submit Button */}
          <div className="flex items-center justify-end space-x-4 pt-4">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              className="border-neutral-700 text-neutral-300 hover:bg-neutral-800"
            >
              {t('booking_btn_cancel')}
            </Button>
          <Button type="submit" disabled={isSubmitting} className="bg-lime-500 hover:bg-lime-600 text-neutral-900 font-bold px-8 transition-all duration-300">
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  {t('booking_btn_submitting')}
                </>
              ) : t('booking_btn_next')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
